/**
 * Advantage+ Creative variants toggle (§9.0.2).
 *
 * Renders as a collapsible panel at the top of the Creative step (new
 * mode) and the Review step (existing mode), mirroring how
 * Advantage Audience sits inside `TargetingStep` in A4.3.2.5 — not as
 * a separate wizard step.
 *
 * When the master toggle is OFF, the wizard sends nothing to the
 * backend's `_build_degrees_of_freedom_spec` (or sends `enabled: false`,
 * which the helper translates to "omit the field"). When ON, sub-flags
 * control which auto-variations Meta may apply at delivery time.
 *
 * The yellow warning Alert at the top of the panel is the first thing
 * the user sees when they enable Advantage+ — it sets expectations that
 * the ad they preview is not the ad that will necessarily serve.
 */
import React from 'react';

import { Alert } from '@/components/ui/Alert';
import { Checkbox } from '@/components/ui/Checkbox';
import type { MetaAdvantagePlusCreativeSpec } from '@/services/api/metaAds';

export interface AdvantagePlusCreativePanelProps {
  value: MetaAdvantagePlusCreativeSpec;
  /** Functional setter form. Multi-field updates (e.g. flipping the
   *  master toggle and conservatively resetting sub-flags) need to
   *  read the previous state — value-only setters race. PR #181
   *  rounds 1+3 lesson. */
  onChange: React.Dispatch<React.SetStateAction<MetaAdvantagePlusCreativeSpec>>;
}

export const AdvantagePlusCreativePanel: React.FC<AdvantagePlusCreativePanelProps> = ({
  value,
  onChange,
}) => {
  const update = (patch: Partial<MetaAdvantagePlusCreativeSpec>) => {
    onChange((prev) => ({ ...prev, ...patch }));
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
      <div>
        <h4 className="text-base font-semibold text-gray-900">
          Advantage+ Creative
        </h4>
        <p className="text-sm text-gray-600 mt-1">
          Let Meta automatically generate variations of your ad — text,
          images, or both — and serve whichever performs best per
          audience.
        </p>
      </div>

      {value.enabled && (
        <Alert variant="warning">
          <p className="text-sm font-medium">
            Variations may differ from your preview
          </p>
          <p className="text-sm mt-1">
            When Advantage+ Creative is on, Meta may generate alternate
            primary text, headlines, or image enhancements. The ad you
            see in Review is one possible version — the served ad can
            differ. Turn off below for a 1:1 match.
          </p>
        </Alert>
      )}

      <fieldset className="space-y-3">
        <legend className="sr-only">Advantage+ Creative options</legend>
        <label className="flex items-start gap-3">
          <input
            type="radio"
            name="advantage-plus-creative-mode"
            value="standard"
            checked={!value.enabled}
            onChange={() => update({ enabled: false })}
            className="mt-1"
            aria-describedby="acp-standard-desc"
          />
          <div>
            <div className="text-sm font-medium text-gray-900">Standard</div>
            <div id="acp-standard-desc" className="text-sm text-gray-600">
              Serve the ad exactly as built. No auto-variation.
            </div>
          </div>
        </label>
        <label className="flex items-start gap-3">
          <input
            type="radio"
            name="advantage-plus-creative-mode"
            value="advantage-plus"
            checked={value.enabled}
            onChange={() => update({ enabled: true })}
            className="mt-1"
            aria-describedby="acp-advantage-desc"
          />
          <div>
            <div className="text-sm font-medium text-gray-900">
              Advantage+ Creative
            </div>
            <div id="acp-advantage-desc" className="text-sm text-gray-600">
              Allow Meta to generate variations.
            </div>
          </div>
        </label>
      </fieldset>

      {value.enabled && (
        <div className="pl-7 space-y-3 border-l-2 border-gray-200">
          <p className="text-sm font-medium text-gray-700">
            Variations Meta may apply
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Checkbox
              id="acp-text-variations"
              checked={value.text_variations}
              onChange={(checked) => update({ text_variations: checked })}
            />
            Text variations (primary text + headline)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Checkbox
              id="acp-image-variations"
              checked={value.image_variations}
              onChange={(checked) => update({ image_variations: checked })}
            />
            Image enhancements (brightness, contrast, crop)
          </label>
        </div>
      )}
    </div>
  );
};
